import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark' | 'auto';
export type EffectiveTheme = 'light' | 'dark';

const STORAGE_KEY = 'sa.theme';
const MEDIA_QUERY = '(prefers-color-scheme: dark)';

type ThemeState = {
  mode: ThemeMode;
  effective: EffectiveTheme;
  setMode: (mode: ThemeMode) => void;
  /** Cycle light → dark → auto → light. */
  cycle: () => void;
};

const ThemeCtx = createContext<ThemeState | null>(null);

function readStoredMode(): ThemeMode {
  if (typeof localStorage === 'undefined') return 'auto';
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' || v === 'auto' ? v : 'auto';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(MEDIA_QUERY).matches;
}

function resolveEffective(mode: ThemeMode): EffectiveTheme {
  if (mode === 'light' || mode === 'dark') return mode;
  return systemPrefersDark() ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode());
  const [effective, setEffective] = useState<EffectiveTheme>(() => resolveEffective(readStoredMode()));

  // Apply the effective theme to <html> so CSS variable cascades cover
  // everything (including inline styles that read var(--foo)).
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', effective);
  }, [effective]);

  // While in auto mode, follow system changes live; otherwise ignore.
  useEffect(() => {
    if (mode !== 'auto') {
      setEffective(mode);
      return;
    }
    const mq = window.matchMedia(MEDIA_QUERY);
    const update = () => setEffective(mq.matches ? 'dark' : 'light');
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode etc. */ }
  }, []);

  const cycle = useCallback(() => {
    setMode(mode === 'light' ? 'dark' : mode === 'dark' ? 'auto' : 'light');
  }, [mode, setMode]);

  return (
    <ThemeCtx.Provider value={{ mode, effective, setMode, cycle }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

/** Small icon-button that cycles light → dark → auto. The icon shows
 *  the CURRENT mode so the user can see what they're on; the title
 *  reveals the next click's destination. */
export function ThemeToggle() {
  const { mode, cycle } = useTheme();
  const label = mode === 'light' ? '☀' : mode === 'dark' ? '☾' : '◐';
  const nextLabel = mode === 'light' ? 'dark' : mode === 'dark' ? 'auto' : 'light';
  return (
    <button
      type="button"
      onClick={cycle}
      title={`Theme: ${mode}. Click for ${nextLabel}.`}
      aria-label={`Theme: ${mode}. Click to switch to ${nextLabel}.`}
      style={{
        background: 'transparent',
        border: '1px solid var(--header-fg-muted)',
        color: 'var(--header-fg)',
        padding: '2px 8px',
        borderRadius: 4,
        marginLeft: 8,
        fontSize: 14,
        cursor: 'pointer',
        lineHeight: 1.2,
      }}
    >
      {label}
    </button>
  );
}
